import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';

const AuthContext = createContext(null);

const ACTIVE = ['active', 'trialing', 'past_due'];

function planOf(user) {
  if (!user) return 'guest';
  if (!user.plan || user.plan === 'free') return 'free';
  if (!user.planStatus || ACTIVE.includes(user.planStatus)) return user.plan;
  return 'free';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user || null);
      return data.user || null;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(() => {
    const plan = planOf(user);
    const emailVerified = Boolean(user && user.emailVerified !== false);
    return {
      user,
      loading,
      refresh,
      setUser,
      logout,
      plan,
      isPro: plan === 'pro' || plan === 'team',
      emailVerified,
      // Signed-in account whose email address is not confirmed yet (free hosted AI is locked until it is).
      needsVerification: Boolean(user) && !emailVerified,
    };
  }, [user, loading, refresh, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
